import React, { useEffect, useState } from 'react';
import { Upload, ChevronDown, Calendar, DollarSign, Fuel as FuelIcon, MapPin, Droplet, X } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { fetchAllRows } from '../../lib/fetchAllRows';
import { useToast } from '../../contexts/ToastContext';
import * as pdfjsLib from 'pdfjs-dist';
import * as XLSX from 'xlsx';

// Set up PDF.js worker from local file
pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

interface FuelTransaction {
  id: string;
  week_start_date: string;
  week_end_date: string;
  driver_name: string;
  card_number?: string;
  transaction_date: string;
  transaction_number?: string;
  location: string;
  product?: string;
  driver_id?: string;
  odometer?: string;
  vehicle_id?: string;
  gallons: number;
  fuel_amount?: number;
  merch_amount?: number;
  amount: number;
  invoice_amount?: number;
  created_at: string;
  statement_id?: string;
}

interface WeekGroup {
  weekLabel: string;
  weekStartDate: string;
  weekEndDate: string;
  transactions: FuelTransaction[];
  totalGallons: number;
  totalAmount: number;
  driverCount: number;
}

export function FuelView() {
  const toast = useToast();
  const [weeks, setWeeks] = useState<WeekGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedWeeks, setExpandedWeeks] = useState<Set<string>>(new Set());
  const [uploading, setUploading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [searchTerms, setSearchTerms] = useState<Record<string, string>>({});
  const [billingFilter, setBillingFilter] = useState<'all' | 'billed' | 'unbilled'>('all');

  useEffect(() => {
    loadFuelData();
  }, []);

  async function loadFuelData() {
    try {
      setLoading(true);
      const data = await fetchAllRows<any>(() => supabase
        .from('pilot_fuel_imports')
        .select('*')
        .order('week_start_date', { ascending: false }));

      // Group by week
      const weekMap = new Map<string, FuelTransaction[]>();
      (data || []).forEach((transaction: FuelTransaction) => {
        const weekKey = `${transaction.week_start_date}_${transaction.week_end_date}`;
        if (!weekMap.has(weekKey)) {
          weekMap.set(weekKey, []);
        }
        weekMap.get(weekKey)!.push(transaction);
      });

      // Convert to WeekGroup array
      const weekGroups: WeekGroup[] = Array.from(weekMap.entries()).map(([weekKey, transactions]) => {
        const weekStartDate = transactions[0].week_start_date;
        const weekEndDate = transactions[0].week_end_date;
        const totalGallons = transactions.reduce((sum, t) => sum + t.gallons, 0);
        const totalAmount = transactions.reduce((sum, t) => sum + t.amount, 0);
        const uniqueDrivers = new Set(transactions.map(t => t.driver_name));

        return {
          weekLabel: `Week of ${new Date(weekStartDate).toLocaleDateString()} - ${new Date(weekEndDate).toLocaleDateString()}`,
          weekStartDate,
          weekEndDate,
          transactions,
          totalGallons,
          totalAmount,
          driverCount: uniqueDrivers.size,
        };
      });

      setWeeks(weekGroups);
    } catch (error) {
      console.error('Error loading fuel data:', error);
      toast.error('Failed to load fuel data');
    } finally {
      setLoading(false);
    }
  }

  const toggleWeek = (weekLabel: string) => {
    const newExpanded = new Set(expandedWeeks);
    if (newExpanded.has(weekLabel)) {
      newExpanded.delete(weekLabel);
    } else {
      newExpanded.add(weekLabel);
    }
    setExpandedWeeks(newExpanded);
  };

  const processPDF = async (file: File) => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      
      let fullText = '';
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        const pageText = textContent.items.map((item: any) => item.str).join(' ');
        fullText += pageText + '\n';
      }
      
      return fullText;
    } catch (error) {
      console.error('Error parsing PDF:', error);
      throw new Error('Failed to parse PDF file');
    }
  };

  const processAIFuelPDF = async (file: File, text: string) => {
    try {
      // Call Edge Function to process fuel text with AI
      const response = await supabase.functions.invoke(
        'process-fuel-statement',
        {
          body: {
            text: text,
            fileName: file.name,
          },
        }
      );
      
      if (response.error) {
        console.error('AI processing error:', response.error);
        throw new Error(response.error.message || 'Failed to process fuel PDF with AI');
      }
      
      return response.data;
    } catch (error: any) {
      console.error('Error calling AI fuel processing:', error);
      throw error;
    }
  };

  const processTabDelimited = async (text: string) => {
    console.log('Processing tab-delimited fuel format');

    // Strip BOM and normalize line endings
    const cleanText = text.replace(/^\uFEFF/, '').replace(/\r\n/g, '\n');
    const lines = cleanText.split('\n').filter(line => line.trim());
    if (lines.length < 2) throw new Error('File is empty or invalid');

    // Parse header row
    const headers = lines[0].split('\t').map(h => h.trim());
    console.log('Headers:', headers);

    // Find column indices. Header names are matched ignoring case, spaces and
    // underscores, and each field accepts a few aliases so a renamed column in
    // the Pilot export doesn't silently map to nothing.
    const norm = (h: string) => h.toLowerCase().replace(/[\s_]+/g, '');
    const normHeaders = headers.map(norm);
    const colIndex = (...names: string[]) => {
      for (const name of names) {
        const idx = normHeaders.indexOf(norm(name));
        if (idx !== -1) return idx;
      }
      return -1;
    };
    const iCard = colIndex('CardNumber', 'Card');
    const iDate = colIndex('TransactionPOSDate', 'TransactionDate', 'Date');
    const iUnit = colIndex('Unit', 'UnitNumber', 'Vehicle', 'VehicleId', 'Truck');
    const iDriverName = colIndex('DriverName', 'Driver');
    const iLocationName = colIndex('LocationName', 'Location');
    const iCity = colIndex('LocationCity', 'City');
    const iState = colIndex('LocationState', 'State');
    const iProductCode = colIndex('ProductCode', 'Product');
    const iQuantity = colIndex('Quantity', 'Gallons', 'Qty');
    // Retail amount (what the pump charged)
    const iAmount = colIndex('Amount', 'RetailAmount', 'Retail');
    // Discounted / invoice amount (what the company actually pays) — this is
    // the price passed to company drivers and to owners under a fuel-discount
    // company. If it is missing, every settlement falls back to retail.
    const iTotal = colIndex('Total', 'TotalAmount', 'InvoiceAmount', 'Invoice', 'NetAmount', 'Net', 'DiscountedAmount', 'Cost');
    // Newer Pilot exports (Sept 2026) drop "Total" and give the discount as a
    // per-row "rebate" column instead: discounted amount = Amount - rebate.
    const iRebate = colIndex('Rebate', 'RebateAmount', 'Discount', 'DiscountAmount');

    console.log('Column indices:', { iCard, iDate, iUnit, iDriverName, iCity, iState, iProductCode, iQuantity, iAmount, iTotal, iRebate });

    if (iCard === -1 || iQuantity === -1 || iAmount === -1) {
      throw new Error(`Fuel file is missing a required column (CardNumber / Quantity / Amount). Columns found: ${headers.join(', ')}`);
    }
    if (iTotal === -1 && iRebate === -1) {
      throw new Error(`Fuel file has neither a "Total" nor a "rebate" column, so discounted fuel prices cannot be applied. Columns found: ${headers.join(', ')}`);
    }

    // Fetch all drivers
    const { data: drivers, error: driversError } = await supabase
      .from('drivers')
      .select('id, first_name, last_name, fuel_card_number');

    if (driversError) {
      console.error('Error fetching drivers:', driversError);
      throw new Error('Failed to fetch driver data');
    }

    // Fetch equipment assignments to map truck unit numbers to drivers
    const { data: assignments } = await supabase
      .from('equipment_assignments')
      .select('driver_id, trucks(unit_number)')
      .eq('is_current', true);

    // Build lookup maps
    const cardToDriver: Record<string, string> = {};
    const truckToDriver: Record<string, string> = {};
    (drivers || []).forEach(driver => {
      const fullName = `${driver.first_name} ${driver.last_name}`.toUpperCase();
      if (driver.fuel_card_number) {
        cardToDriver[driver.fuel_card_number] = fullName;
      }
      // Find truck assignment for this driver
      const assignment = assignments?.find(a => a.driver_id === driver.id);
      const truck = assignment?.trucks as any;
      const unitNumber = Array.isArray(truck) ? truck[0]?.unit_number : truck?.unit_number;
      if (unitNumber) {
        truckToDriver[String(unitNumber)] = fullName;
      }
    });

    console.log('Loaded', Object.keys(cardToDriver).length, 'fuel card mappings and', Object.keys(truckToDriver).length, 'truck mappings');

    // Determine week range from transaction dates
    const transactions: any[] = [];
    let minDate = '';
    let maxDate = '';

    for (let i = 1; i < lines.length; i++) {
      const parts = lines[i].split('\t');

      // Skip subtotal rows (rows where CardNumber is empty or most fields are empty)
      const cardNumber = parts[iCard]?.trim() || '';
      if (!cardNumber || cardNumber.length < 5) continue;

      const transDate = parts[iDate]?.trim() || '';
      const unit = parts[iUnit]?.trim() || '';
      const driverNameField = parts[iDriverName]?.trim() || '';
      const locationName = parts[iLocationName]?.trim() || '';
      const city = parts[iCity]?.trim() || '';
      const state = parts[iState]?.trim() || '';
      const productCode = parts[iProductCode]?.trim() || '';
      const quantity = parseFloat(parts[iQuantity]?.trim() || '0') || 0;
      const amount = parseFloat(parts[iAmount]?.trim() || '0') || 0;
      // Discounted amount: explicit Total column if present, otherwise Amount - rebate.
      let total = iTotal !== -1 ? (parseFloat(parts[iTotal]?.trim() || '0') || 0) : 0;
      if (!total && iRebate !== -1) {
        const rebate = parseFloat(parts[iRebate]?.trim() || '0') || 0;
        total = Math.round((amount - rebate) * 100) / 100;
      }

      // Skip rows with no amount
      if (amount === 0) continue;

      // Map product code
      const product = productCode === 'ULSD' ? 'Truck Diesel' :
                      productCode === 'DEFD' ? 'Diesel Exhaust Fluid' : productCode || 'Unknown';

      // Resolve driver name: use field value, then card lookup, then truck/unit lookup
      let driverName = driverNameField;
      if (!driverName) {
        driverName = cardToDriver[cardNumber] || truckToDriver[unit] || '';
      }
      if (!driverName) {
        driverName = `UNIT ${unit}`;
      }

      // Track date range
      if (!minDate || transDate < minDate) minDate = transDate;
      if (!maxDate || transDate > maxDate) maxDate = transDate;

      const location = `${locationName} ${city} ${state}`.trim();

      transactions.push({
        week_start_date: '', // will be set below
        week_end_date: '',
        driver_name: driverName.toUpperCase(),
        card_number: cardNumber,
        transaction_date: transDate,
        location: location,
        city: city,
        state: state,
        product: product,
        vehicle_id: unit,
        gallons: quantity,
        amount: amount,
        invoice_amount: total,
      });
    }

    // Calculate week dates from the transaction date range (Monday-Sunday)
    if (minDate) {
      const startObj = new Date(minDate + 'T00:00:00');
      const dayOfWeek = startObj.getDay();
      const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
      const monday = new Date(startObj);
      monday.setDate(startObj.getDate() + mondayOffset);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);

      const weekStart = monday.toISOString().split('T')[0];
      const weekEnd = sunday.toISOString().split('T')[0];

      transactions.forEach(t => {
        t.week_start_date = weekStart;
        t.week_end_date = weekEnd;
      });

      console.log('Week range:', weekStart, 'to', weekEnd);
    }

    console.log(`Processed ${transactions.length} tab-delimited transactions`);

    const missingTotal = transactions.filter(t => !t.invoice_amount).length;
    if (missingTotal > 0) {
      toast.error(`${missingTotal} of ${transactions.length} fuel rows have no discounted (Total) amount — those will be charged at retail on settlements.`, 10000);
    }
    return transactions;
  };

  const processCSVDirectly = async (text: string) => {
    console.log('🔍 Processing CSV directly');
    console.log('CSV text length:', text.length);
    console.log('First 500 chars:', text.substring(0, 500));
    
    // Fetch all drivers with fuel card numbers from database
    const { data: drivers, error: driversError } = await supabase
      .from('drivers')
      .select('id, first_name, last_name, fuel_card_number')
      .not('fuel_card_number', 'is', null);
    
    if (driversError) {
      console.error('Error fetching drivers:', driversError);
      throw new Error('Failed to fetch driver data');
    }
    
    // Build driver map from database
    const driverMap: { [key: string]: string } = {};
    (drivers || []).forEach(driver => {
      if (driver.fuel_card_number) {
        const fullName = `${driver.first_name} ${driver.last_name}`.toUpperCase();
        driverMap[driver.fuel_card_number] = fullName;
      }
    });
    
    console.log('📋 Loaded driver map with', Object.keys(driverMap).length, 'fuel cards');

    const lines = text.split('\n').filter(line => line.trim());
    console.log('Total lines:', lines.length);
    
    const dataLines = lines.filter(line => line.startsWith('D|'));
    console.log('Data lines (D|):', dataLines.length);
    
    const transactions: any[] = [];
    
    // Extract billing date from header
    let billingDate = new Date().toISOString().split('T')[0];
    const headerLine = lines.find(line => line.startsWith('H|'));
    if (headerLine) {
      const dateMatch = headerLine.match(/\|(\d{8})\|/);
      if (dateMatch) {
        const dateStr = dateMatch[1];
        const year = dateStr.slice(0, 4);
        const month = dateStr.slice(4, 6);
        const day = dateStr.slice(6, 8);
        billingDate = `${year}-${month}-${day}`;
      }
    }
    
    // Calculate week dates
    const weekStartDate = billingDate;
    const weekEndObj = new Date(billingDate);
    weekEndObj.setDate(weekEndObj.getDate() + 7);
    const weekEndDate = weekEndObj.toISOString().split('T')[0];
    
    // Process transaction lines
    let processedCount = 0;
    let skippedCount = 0;
    
    for (const line of lines) {
      if (line.startsWith('D|')) {
        const parts = line.split('|');
        
        if (parts.length >= 22) {
          const cardNumber = parts[1] || '';
          const vehicleId = parts[2] || '';
          const locationNumber = parts[3] || '';
          const city = parts[4] || '';
          const state = parts[5] || '';
          const dateStr = parts[11] || '';
          
          const quantity = parseFloat(parts[16]) || 0;
          const retailAmount = parseFloat(parts[18]) || 0;
          const invoiceAmount = parseFloat(parts[19]) || 0;
          const productCode = parts[20] || '';
          const product = productCode === '020' ? 'Truck Diesel' : 
                        productCode === '140' ? 'Diesel Exhaust Fluid' : 'Unknown';
          
          // Format transaction date
          let transactionDate = billingDate;
          if (dateStr.length === 8) {
            const year = dateStr.slice(0, 4);
            const month = dateStr.slice(4, 6);
            const day = dateStr.slice(6, 8);
            transactionDate = `${year}-${month}-${day}`;
          }
          
          const driverName = driverMap[cardNumber] || `DRIVER ${cardNumber}`;
          
          if (processedCount < 3) {
            console.log('Sample transaction:', {
              cardNumber,
              quantity,
              retailAmount,
              driverName,
              hasCardNumber: !!cardNumber,
              hasQuantity: quantity > 0
            });
          }
          
          if (cardNumber && quantity > 0) {
            transactions.push({
              week_start_date: weekStartDate,
              week_end_date: weekEndDate,
              driver_name: driverName,
              card_number: cardNumber,
              transaction_date: transactionDate,
              location: `${locationNumber} ${city} ${state}`.trim(),
              product: product,
              vehicle_id: vehicleId,
              gallons: quantity,
              amount: retailAmount,
              invoice_amount: invoiceAmount,
            });
            processedCount++;
          } else {
            skippedCount++;
            if (skippedCount < 3) {
              console.log('Skipped transaction:', { cardNumber, quantity });
            }
          }
        }
      }
    }
    
    console.log(`✅ Processed ${processedCount} transactions, skipped ${skippedCount}`);
    return transactions;
  };

  const processFile = async (file: File) => {
    setUploading(true);
    setShowUploadModal(false);

    try {
      let text = '';
      
      // Check file type and process accordingly
      if (file.name.toLowerCase().endsWith('.pdf')) {
        // Extract text from PDF on client side
        toast.info('📄 Reading PDF file...', 3000);
        const pdfText = await processPDF(file);
        
        // Process with AI
        toast.info('🤖 AI is analyzing fuel transactions... This may take 30-60 seconds for large files.', 60000);
        const result = await processAIFuelPDF(file, pdfText);
        
        if (!result || !result.transactions || result.transactions.length === 0) {
          toast.error('No valid fuel data found in PDF');
          setUploading(false);
          return;
        }

        toast.success(`✅ Successfully imported ${result.transactions.length} fuel transactions from PDF!`);
        loadFuelData();
        setUploading(false);
        return;
      }
      
      // Read file content — handle XLSX/XLS by converting to tab-delimited text
      const isExcel = file.name.toLowerCase().endsWith('.xlsx') || file.name.toLowerCase().endsWith('.xls');
      if (isExcel) {
        toast.info('Reading Excel file...', 3000);
        const arrayBuffer = await file.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        // Convert to tab-delimited text so existing parsers can handle it
        text = XLSX.utils.sheet_to_csv(firstSheet, { FS: '\t' });
        console.log('Converted XLSX to tab-delimited text, length:', text.length);
        console.log('First 500 chars:', text.substring(0, 500));
      } else {
        text = await file.text();
      }

      // Check if it's a tab-delimited format (new Pilot format with CardNumber header)
      // Strip BOM and trim for reliable detection
      const cleanedText = text.replace(/^\uFEFF/, '').trim();
      const firstLine = cleanedText.split('\n')[0] || '';
      if (firstLine.includes('CardNumber') && firstLine.includes('\t')) {
        toast.info('Processing tab-delimited fuel file...', 3000);
        const transactions = await processTabDelimited(cleanedText);

        if (transactions.length === 0) {
          toast.error('No valid fuel data found in file');
          setUploading(false);
          return;
        }

        const { error } = await supabase
          .from('pilot_fuel_imports')
          .insert(transactions);

        if (error) throw error;

        toast.success(`Successfully imported ${transactions.length} fuel transactions!`);
        loadFuelData();
        setUploading(false);
        return;
      }

      // Check if it's a pipe-delimited CSV (Pilot format)
      if (text.includes('D|') && text.includes('H|')) {
        // Process directly on client side
        toast.info('📄 Processing fuel CSV file...', 3000);
        const transactions = await processCSVDirectly(text);
        
        if (transactions.length === 0) {
          toast.error('No valid fuel data found in CSV');
          setUploading(false);
          return;
        }

        // Insert into database
        const { error } = await supabase
          .from('pilot_fuel_imports')
          .insert(transactions);

        if (error) throw error;

        toast.success(`✅ Successfully imported ${transactions.length} fuel transactions from CSV!`);
        loadFuelData();
        setUploading(false);
        return;
      }
      
      // Standard comma-delimited CSV processing
      const lines = text.split('\n').filter(line => line.trim());
      
      if (lines.length < 2) {
        toast.error('CSV file is empty or invalid');
        setUploading(false);
        return;
      }

      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
      const transactions = lines.slice(1).map(line => {
        const values = line.split(',').map(v => v.trim());
        const row: any = {};
        headers.forEach((header, index) => {
          row[header] = values[index] || '';
        });

        return {
          week_start_date: row.week_start_date || row.week_start || new Date().toISOString().split('T')[0],
          week_end_date: row.week_end_date || row.week_end || new Date().toISOString().split('T')[0],
          driver_name: row.driver_name || row.driver || row.name || '',
          location: row.location || row.city || row.state || '',
          gallons: parseFloat(row.gallons || row.quantity || '0'),
          amount: parseFloat(row.amount || row.total || row.price || '0'),
          transaction_date: row.transaction_date || row.date || new Date().toISOString().split('T')[0],
        };
      }).filter(t => t.driver_name && t.amount > 0);

      if (transactions.length === 0) {
        toast.error('No valid transactions found in CSV file');
        setUploading(false);
        return;
      }

      const { error } = await supabase
        .from('pilot_fuel_imports')
        .insert(transactions);

      if (error) throw error;

      toast.success(`Successfully imported ${transactions.length} fuel transactions!`);
      loadFuelData();
    } catch (error: any) {
      console.error('Error uploading fuel data:', error);
      toast.error(error?.message ? `Failed to upload fuel data: ${error.message}` : 'Failed to upload fuel data', 10000);
    } finally {
      setUploading(false);
    }
  };

  const handleFileInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      processFile(file);
      event.target.value = '';
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files?.[0];
    if (file) {
      processFile(file);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="flex-none border-b border-gray-200 bg-white px-6 py-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Fuel Transactions</h1>
            <p className="text-sm text-gray-500 mt-1">Import and manage weekly fuel data from PILOT (CSV or PDF)</p>
          </div>
          <button
            onClick={() => setShowUploadModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Upload className="w-5 h-5" />
            Import File
          </button>
        </div>
        
        {/* Billing Status Filter */}
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-gray-700">Filter:</span>
          <div className="flex gap-2">
            <button
              onClick={() => setBillingFilter('all')}
              className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                billingFilter === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All Transactions
            </button>
            <button
              onClick={() => setBillingFilter('billed')}
              className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                billingFilter === 'billed'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Billed Only
            </button>
            <button
              onClick={() => setBillingFilter('unbilled')}
              className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                billingFilter === 'unbilled'
                  ? 'bg-gray-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Unbilled Only
            </button>
          </div>
        </div>
      </div>

      {/* Weekly Groups */}
      <div className="flex-1 overflow-auto p-6">
        {weeks.length === 0 ? (
          <div className="text-center py-12">
            <FuelIcon className="w-12 h-12 mx-auto mb-3 text-gray-400" />
            <p className="text-lg font-medium text-gray-900">No fuel data found</p>
            <p className="text-sm text-gray-500 mt-1">Upload a CSV file to get started</p>
          </div>
        ) : (
          <div className="space-y-2">
            {weeks.map((week) => (
              <div key={week.weekLabel} className="border border-gray-200 rounded-lg overflow-hidden">
                {/* Week Header - Collapsible */}
                <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-blue-50 to-blue-100 border-blue-500 border-l-4">
                  <div 
                    onClick={() => toggleWeek(week.weekLabel)}
                    className="flex-1 cursor-pointer"
                  >
                    <h3 className="font-bold text-slate-900 text-base">{week.weekLabel}</h3>
                    <p className="text-xs text-slate-600">
                      {week.driverCount} drivers • {week.totalGallons.toFixed(2)} gallons • ${week.totalAmount.toFixed(2)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (confirm(`Delete all transactions for ${week.weekLabel}?`)) {
                          try {
                            const { error } = await supabase
                              .from('pilot_fuel_imports')
                              .delete()
                              .gte('week_start_date', week.weekStartDate)
                              .lte('week_start_date', week.weekEndDate);
                            
                            if (error) throw error;
                            
                            toast.success('Week deleted successfully');
                            loadFuelData();
                          } catch (error) {
                            console.error('Error deleting week:', error);
                            toast.error('Failed to delete week');
                          }
                        }
                      }}
                      className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                      title="Delete this week"
                    >
                      <X className="w-4 h-4" />
                    </button>
                    <div 
                      onClick={() => toggleWeek(week.weekLabel)}
                      className={`transform transition-transform duration-200 cursor-pointer ${expandedWeeks.has(week.weekLabel) ? 'rotate-180' : ''}`}
                    >
                      <ChevronDown className="w-5 h-5 text-slate-600" />
                    </div>
                  </div>
                </div>

                {/* Week Transactions - Expandable */}
                {expandedWeeks.has(week.weekLabel) && (
                  <div className="bg-white">
                    {/* Search Box */}
                    <div className="px-4 py-3 border-b border-gray-200">
                      <input
                        type="text"
                        placeholder="Search transactions (driver, location, card #, etc.)..."
                        value={searchTerms[week.weekLabel] || ''}
                        onChange={(e) => setSearchTerms({ ...searchTerms, [week.weekLabel]: e.target.value })}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <table className="w-full text-xs">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">Card #</th>
                          <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                          <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">Store Location</th>
                          <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                          <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">Vehicle ID</th>
                          <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">Qty</th>
                          <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">Retail $</th>
                          <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">Invoice $</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          const filteredTransactions = week.transactions.filter((transaction) => {
                            // Apply billing status filter
                            if (billingFilter === 'billed' && !transaction.statement_id) return false;
                            if (billingFilter === 'unbilled' && transaction.statement_id) return false;
                            
                            // Apply search filter
                            const searchTerm = (searchTerms[week.weekLabel] || '').toLowerCase();
                            if (!searchTerm) return true;
                            
                            return (
                              transaction.driver_name?.toLowerCase().includes(searchTerm) ||
                              transaction.card_number?.toLowerCase().includes(searchTerm) ||
                              transaction.transaction_number?.toLowerCase().includes(searchTerm) ||
                              transaction.location?.toLowerCase().includes(searchTerm) ||
                              transaction.product?.toLowerCase().includes(searchTerm) ||
                              transaction.driver_id?.toLowerCase().includes(searchTerm) ||
                              transaction.vehicle_id?.toLowerCase().includes(searchTerm)
                            );
                          });

                          // Group by driver name
                          const groupedByDriver: Record<string, FuelTransaction[]> = {};
                          filteredTransactions.forEach((transaction) => {
                            const driverName = transaction.driver_name || 'Unknown Driver';
                            if (!groupedByDriver[driverName]) {
                              groupedByDriver[driverName] = [];
                            }
                            groupedByDriver[driverName].push(transaction);
                          });

                          return Object.entries(groupedByDriver).map(([driverName, driverTransactions]) => {
                            const driverTotal = driverTransactions.reduce((sum, t) => sum + t.gallons, 0);
                            const driverAmount = driverTransactions.reduce((sum, t) => sum + t.amount, 0);
                            const driverInvoiceAmount = driverTransactions.reduce((sum, t) => sum + (t.invoice_amount || 0), 0);
                            const difference = driverAmount - driverInvoiceAmount;

                            return (
                              <React.Fragment key={driverName}>
                                {/* Driver Subtotal Header */}
                                <tr className="bg-blue-50 border-t-2 border-blue-200">
                                  <td colSpan={13} className="px-2 py-2">
                                    <div className="flex justify-between items-center">
                                      <span className="font-bold text-xs text-gray-900">
                                        Subtotal for Card - {driverName}
                                      </span>
                                      <span className="text-xs text-gray-700">
                                        {driverTotal.toFixed(3)} gallons • ${driverAmount.toFixed(2)} • Invoice: ${driverInvoiceAmount.toFixed(2)} • Difference: <span className={difference >= 0 ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>${difference.toFixed(2)}</span>
                                      </span>
                                    </div>
                                  </td>
                                </tr>
                                {/* Driver Transactions */}
                                {driverTransactions.map((transaction) => (
                                  <tr key={transaction.id} className={transaction.statement_id ? "bg-green-50 hover:bg-green-100" : "hover:bg-gray-50"}>
                                    <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-900">{transaction.card_number || '-'}</td>
                                    <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-600">{new Date(transaction.transaction_date).toLocaleDateString()}</td>
                                    <td className="px-2 py-2 text-xs text-gray-600">{transaction.location}</td>
                                    <td className="px-2 py-2 text-xs text-gray-600">{transaction.product || '-'}</td>
                                    <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-600">{transaction.vehicle_id || '-'}</td>
                                    <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-900">{transaction.gallons.toFixed(3)}</td>
                                    <td className="px-2 py-2 whitespace-nowrap text-xs font-semibold text-gray-900">${transaction.amount.toFixed(2)}</td>
                                    <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-900">${transaction.invoice_amount?.toFixed(2) || '0.00'}</td>
                                  </tr>
                                ))}
                              </React.Fragment>
                            );
                          });
                        })()}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Import Fuel Data</h2>
              <button
                onClick={() => setShowUploadModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6">
              {/* Drag and Drop Zone */}
              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                  isDragging
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <p className="text-lg font-medium text-gray-900 mb-2">
                  {uploading ? 'Uploading...' : 'Drag and drop your CSV or PDF file here'}
                </p>
                <p className="text-sm text-gray-500 mb-4">or</p>
                <label className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer">
                  <Upload className="w-5 h-5" />
                  Choose File
                  <input
                    type="file"
                    accept=".csv,.xlsx,.xls,.pdf"
                    onChange={handleFileInput}
                    disabled={uploading}
                    className="hidden"
                  />
                </label>
                <p className="text-xs text-gray-500 mt-4">
                  Supported formats: CSV, XLSX, XLS, PDF
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
